import { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import axiosInstance from '../../config/axios.config';
import { toast } from 'react-toastify';
import ReportViewer from '../reports/ReportViewer';

/**
 * Tree Reports Modal (Unified for MLM and OrgChart)
 * Displays reports with tree-based access control
 * Supports: usermlm, orgposition, orguser access types
 *
 * @param {boolean} visible - Modal visibility state
 * @param {function} onHide - Modal close handler
 * @param {number|null} userId - Target user ID (for usermlm/orguser)
 * @param {number|null} positionId - Target position ID (for orgposition)
 * @param {string} userName - Target user/position name for display
 * @param {string} reportType - Report type filter: 'my', 'network', or 'total'
 * @param {string} accessType - Access type: 'usermlm' | 'orgposition' | 'orguser'
 * @param {string} filterType - For orgposition: 'whole_branch' | 'this_position_only'
 */
const MLMUserReportsModal = ({
    visible,
    onHide,
    userId,
    positionId = null,
    userName,
    reportType = 'my',
    accessType = 'usermlm',
    filterType = 'whole_branch'
}) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [showReportView, setShowReportView] = useState(false);
    const [reportDetails, setReportDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Network inclusion toggle (for usermlm/orguser)
    const [includeNetwork, setIncludeNetwork] = useState(false);

    // Position filter toggle (for orgposition): false = whole_branch, true = this_position_only
    const [thisPositionOnly, setThisPositionOnly] = useState(false);

    // Pagination
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filters
    const [searchFromDate, setSearchFromDate] = useState(null);
    const [searchToDate, setSearchToDate] = useState(null);
    const [selectedReportType, setSelectedReportType] = useState(null);
    const [selectedAssignmentStatus, setSelectedAssignmentStatus] = useState(null);

    // Sorting
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    // Report type options
    const reportTypes = [
        { label: 'All Reports', value: null },
        { label: 'Equifax', value: 'EQUIFAX_REPORT' },
        { label: 'Experian', value: 'EXPERIAN_BUREAU' },
        { label: 'Employment History', value: 'EMPLOYMENT_HISTORY' },
        { label: 'Bank Statement Analysis', value: 'BANK_STATEMENT_ANALYSIS' },
        { label: 'Mobile Bureau Lookup', value: 'MOBILE_BUREAU_LOOKUP' },
        { label: 'PAN Verification', value: 'PAN_VERIFICATION' },
        { label: 'Voter ID Verification', value: 'VOTER_ID_VERIFICATION' },
        { label: 'CA Membership', value: 'CA_MEMBERSHIP_VERIFICATION' },
        { label: 'Driving License', value: 'DL_DETAILS' },
        { label: 'RC Details', value: 'RC_DETAILS' }
    ];

    // Assignment status options
    const assignmentStatusOptions = [
        { label: 'All', value: null },
        { label: 'Assigned', value: 'assigned' },
        { label: 'Unassigned', value: 'unassigned' }
    ];

    // Fetch reports when modal opens or filters change
    useEffect(() => {
        if (visible) {
            fetchReports();
        }
    }, [visible, userId, positionId, accessType, thisPositionOnly, includeNetwork, first, rows, sortField, sortOrder]);

    // Reset state when modal opens/closes and initialize toggles based on reportType
    useEffect(() => {
        if (visible) {
            if (accessType === 'orgposition') {
                // For orgposition: set thisPositionOnly based on reportType
                if (reportType === 'my') {
                    // My Reports: show only this position's own reports
                    setThisPositionOnly(true);
                } else if (reportType === 'network') {
                    // Network Reports: show whole branch reports
                    setThisPositionOnly(false);
                } else {
                    // Total Reports: use filterType prop
                    setThisPositionOnly(filterType === 'this_position_only');
                }
                setIncludeNetwork(true);
            } else {
                // For usermlm/orguser: set includeNetwork based on reportType
                if (reportType === 'my') {
                    // My Reports: show only user's own reports (exclude network)
                    setIncludeNetwork(false);
                } else {
                    // Network or Total Reports: include network reports
                    setIncludeNetwork(true);
                }
            }

            // Reset filters when opening
            setFirst(0);
            setSelectedReportType(null);
            setSelectedAssignmentStatus(null);
            setSearchFromDate(null);
            setSearchToDate(null);
        } else {
            // Clear data when closing
            setReports([]);
            setSelectedReport(null);
            setShowReportView(false);
        }
    }, [visible, reportType, accessType, filterType]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            // Build request body for unified tree reports API
            const requestBody = {
                access_type: accessType,
                page: Math.floor(first / rows) + 1,
                per_page: rows,
                include_network: includeNetwork
            };

            // Set target_id based on access type
            if (accessType === 'orgposition') {
                // For orgposition, use positionId
                if (positionId !== null && positionId !== undefined) {
                    requestBody.target_id = parseInt(positionId, 10);
                }
                // Use toggle state for filter_type
                requestBody.filter_type = thisPositionOnly ? 'this_position_only' : 'whole_branch';
            } else {
                // For usermlm/orguser, use userId
                if (userId !== null && userId !== undefined) {
                    requestBody.target_id = parseInt(userId, 10);
                }
            }

            // Add sorting
            if (sortField) {
                requestBody.sort_field = sortField;
                requestBody.sort_order = sortOrder === 1 ? 'asc' : 'desc';
            }

            // Add filters (skip when "All" is selected - null/empty)
            if (selectedReportType && selectedReportType !== '') {
                requestBody.report_name = selectedReportType;
            }

            if (selectedAssignmentStatus && selectedAssignmentStatus !== '') {
                requestBody.attachment_status = selectedAssignmentStatus;
            }

            if (searchFromDate) {
                requestBody.date_from = searchFromDate.toISOString().split('T')[0];
            }

            if (searchToDate) {
                requestBody.date_to = searchToDate.toISOString().split('T')[0];
            }

            console.log('Fetching tree reports with body:', requestBody);

            // POST request to unified tree reports API
            const response = await axiosInstance.post('/tree/reports', requestBody);

            if (response.data.success) {
                setReports(response.data.data);
                const pagination = response.data.pagination;
                if (pagination) {
                    setTotalRecords(pagination.total);
                } else {
                    setTotalRecords(response.data.data.length);
                }
            } else {
                toast.error('Failed to fetch reports');
            }
        } catch (error) {
            console.error('Error fetching tree reports:', error);
            if (error.response?.status === 403) {
                toast.error('Access denied: User is not in your network');
            } else if (error.response?.status === 404) {
                toast.error('User not found');
            } else if (error.response?.status === 400) {
                toast.error(error.response?.data?.message || 'Invalid request');
            } else {
                toast.error('Error fetching reports');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setFirst(0); // Reset to first page
        fetchReports();
    };

    const handleClearFilters = () => {
        setSearchFromDate(null);
        setSearchToDate(null);
        setSelectedReportType(null);
        setSelectedAssignmentStatus(null);
        setSortField(null);
        setSortOrder(null);
        setFirst(0);
        // Reset toggles to initial state based on accessType
        if (accessType === 'orgposition') {
            setThisPositionOnly(filterType === 'this_position_only');
        }
        // All access types default to include network = true
        setIncludeNetwork(true);
    };

    const handleViewReport = async (report) => {
        // Toggle if clicking the same report
        if (selectedReport?.report_id === report.report_id && showReportView) {
            setShowReportView(false);
            setSelectedReport(null);
            setReportDetails(null);
            return;
        }

        setSelectedReport(report);
        setShowReportView(true);
        setDetailsLoading(true);

        try {
            const reportId = report.report_id || report.id; // Use report_id if available, fallback to id
            console.log('Fetching report details for ID:', reportId);
            console.log('Report object:', report);
            // Use accessType directly - backend now supports all 3 types
            // usermlm, orgposition, orguser
            const response = await axiosInstance.get(`/report-history/details/${reportId}?tree=${accessType}`);
            console.log('Report details response:', response.data);

            if (response.data.success) {
                const reportData = response.data.data;
                console.log('Setting report details:', reportData);
                console.log('Report JSON:', reportData.report_json);
                console.log('Report Name:', reportData.report_name);

                // Ensure report_json is available
                if (!reportData.report_json) {
                    console.warn('No report_json in response, checking api_response');
                    if (reportData.api_response) {
                        reportData.report_json = reportData.api_response;
                    }
                }

                setReportDetails(reportData);
            } else {
                console.error('Failed response:', response.data);
                toast.error(response.data.message || 'Failed to fetch report details');
                setShowReportView(false);
            }
        } catch (error) {
            console.error('Error loading report details:', error);
            console.error('Error response:', error.response?.data);
            toast.error(error.response?.data?.message || 'Error loading report details');
            setShowReportView(false);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleDownloadReport = async (report) => {
        try {
            if (report.download_url) {
                // Use the download URL from backend
                if (report.download_url.startsWith('http')) {
                    window.open(report.download_url, '_blank');
                } else {
                    // Relative URL - use axios
                    const response = await axiosInstance.get(report.download_url, {
                        responseType: 'blob'
                    });

                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${report.report_name}_${report.report_id}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
                toast.success('Download started');
            } else {
                toast.error('Download URL not available');
            }
        } catch (error) {
            console.error('Error downloading report:', error);
            toast.error('Error downloading report');
        }
    };

    // Column templates
    const reportNameTemplate = (rowData) => {
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold">{rowData.report_name}</span>
                {rowData.input_summary && (
                    <span className="text-sm text-600">{rowData.input_summary}</span>
                )}
            </div>
        );
    };

    const fetchedDateTemplate = (rowData) => {
        return <span className="text-sm">{rowData.fetched_date}</span>;
    };

    const actionTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text p-button-info"
                    tooltip="View Report"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleViewReport(rowData)}
                />
                <Button
                    icon="pi pi-download"
                    className="p-button-rounded p-button-text p-button-success"
                    tooltip="Download Report"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleDownloadReport(rowData)}
                />
            </div>
        );
    };

    // Dialog header
    const dialogHeader = (
        <div className="flex align-items-center gap-3">
            <i className="pi pi-file text-2xl" style={{ color: '#4F46E5' }}></i>
            <div>
                <div className="text-xl font-bold">Reports - {userName}</div>
                <div className="text-sm text-600 font-normal">View and download reports</div>
            </div>
        </div>
    );

    // Dialog footer with close button
    const dialogFooter = (
        <div className="flex justify-content-end">
            <Button label="Close" icon="pi pi-times" onClick={onHide} className="p-button-text" />
        </div>
    );


    return (
        <>
            <Dialog
                visible={visible}
                onHide={onHide}
                header={dialogHeader}
                footer={dialogFooter}
                style={{ width: '95vw', maxHeight: '90vh' }}
                maximizable
                modal
                className="mlm-user-reports-modal"
            >
                {/* Filters Section */}
                <div className="surface-100 border-round p-3 mb-3">
                    {/* Desktop Layout - Original horizontal layout */}
                    <div className="hidden md:block">
                        <div className="flex flex-wrap align-items-center gap-3">
                            {/* Network Toggle - Only for usermlm and orguser access types */}
                            {(accessType === 'usermlm' || accessType === 'orguser') && (
                                <div className="flex align-items-center gap-2">
                                    <label htmlFor="networkToggle" className="text-sm font-semibold text-700 white-space-nowrap">
                                        Include Network
                                    </label>
                                    <InputSwitch
                                        inputId="networkToggle"
                                        checked={includeNetwork}
                                        onChange={(e) => {
                                            setIncludeNetwork(e.value);
                                            setFirst(0);
                                        }}
                                        tooltip={includeNetwork ? "Showing reports from user + network" : "Showing only user's reports"}
                                        tooltipOptions={{ position: 'bottom' }}
                                    />
                                </div>
                            )}

                            {/* Position Filter Toggle - Only for orgposition access type */}
                            {accessType === 'orgposition' && (
                                <div className="flex align-items-center gap-2">
                                    <label htmlFor="positionFilterToggle" className="text-sm font-semibold text-700 white-space-nowrap">
                                        This Position Only
                                    </label>
                                    <InputSwitch
                                        inputId="positionFilterToggle"
                                        checked={thisPositionOnly}
                                        onChange={(e) => {
                                            setThisPositionOnly(e.value);
                                            setFirst(0);
                                        }}
                                        tooltip={thisPositionOnly ? "Showing reports from this position only" : "Showing reports from whole branch"}
                                        tooltipOptions={{ position: 'bottom' }}
                                    />
                                </div>
                            )}

                            {/* Date Range */}
                            <Calendar
                                value={searchFromDate}
                                onChange={(e) => setSearchFromDate(e.value)}
                                dateFormat="dd-mm-yy"
                                placeholder="From Date"
                                showIcon
                                style={{ width: '160px' }}
                            />

                            <Calendar
                                value={searchToDate}
                                onChange={(e) => setSearchToDate(e.value)}
                                dateFormat="dd-mm-yy"
                                placeholder="To Date"
                                showIcon
                                style={{ width: '160px' }}
                            />

                            {/* Report Type */}
                            <Dropdown
                                value={selectedReportType}
                                options={reportTypes}
                                onChange={(e) => setSelectedReportType(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Report Type"
                                showClear
                                style={{ width: '180px' }}
                            />

                            {/* Assignment Status */}
                            <Dropdown
                                value={selectedAssignmentStatus}
                                options={assignmentStatusOptions}
                                onChange={(e) => setSelectedAssignmentStatus(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Status"
                                showClear
                                style={{ width: '150px' }}
                            />

                            {/* Action Buttons */}
                            <Button
                                icon="pi pi-filter-slash"
                                label="Clear"
                                onClick={handleClearFilters}
                                className="p-button-outlined p-button-secondary"
                            />
                            <Button
                                icon="pi pi-search"
                                label="Search"
                                onClick={handleSearch}
                                className="p-button-primary"
                            />
                        </div>
                    </div>

                    {/* Mobile Layout - Stacked with toggle at bottom */}
                    <div className="block md:hidden">
                        <div className="grid">
                            {/* Row 1: Date Filters - 2 per row */}
                            <div className="col-6">
                                <label className="block text-sm font-medium text-600 mb-1">From Date</label>
                                <Calendar
                                    value={searchFromDate}
                                    onChange={(e) => setSearchFromDate(e.value)}
                                    dateFormat="dd-mm-yy"
                                    placeholder="Select date"
                                    showIcon
                                    className="w-full"
                                />
                            </div>
                            <div className="col-6">
                                <label className="block text-sm font-medium text-600 mb-1">To Date</label>
                                <Calendar
                                    value={searchToDate}
                                    onChange={(e) => setSearchToDate(e.value)}
                                    dateFormat="dd-mm-yy"
                                    placeholder="Select date"
                                    showIcon
                                    className="w-full"
                                />
                            </div>

                            {/* Row 2: Dropdown Filters - 2 per row */}
                            <div className="col-6">
                                <label className="block text-sm font-medium text-600 mb-1">Report Type</label>
                                <Dropdown
                                    value={selectedReportType}
                                    options={reportTypes}
                                    onChange={(e) => setSelectedReportType(e.value)}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="All Reports"
                                    showClear
                                    className="w-full"
                                />
                            </div>
                            <div className="col-6">
                                <label className="block text-sm font-medium text-600 mb-1">Status</label>
                                <Dropdown
                                    value={selectedAssignmentStatus}
                                    options={assignmentStatusOptions}
                                    onChange={(e) => setSelectedAssignmentStatus(e.value)}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="All Status"
                                    showClear
                                    className="w-full"
                                />
                            </div>

                            {/* Row 3: Toggle + Action Buttons */}
                            <div className="col-12 flex justify-content-between align-items-center gap-2 mt-2">
                                {/* Mobile Toggle */}
                                {(accessType === 'usermlm' || accessType === 'orguser') && (
                                    <div className="flex align-items-center gap-2">
                                        <label htmlFor="networkToggleMobile" className="text-xs font-semibold text-700 white-space-nowrap">
                                            Network
                                        </label>
                                        <InputSwitch
                                            inputId="networkToggleMobile"
                                            checked={includeNetwork}
                                            onChange={(e) => {
                                                setIncludeNetwork(e.value);
                                                setFirst(0);
                                            }}
                                        />
                                    </div>
                                )}
                                {accessType === 'orgposition' && (
                                    <div className="flex align-items-center gap-2">
                                        <label htmlFor="positionToggleMobile" className="text-xs font-semibold text-700 white-space-nowrap">
                                            Position Only
                                        </label>
                                        <InputSwitch
                                            inputId="positionToggleMobile"
                                            checked={thisPositionOnly}
                                            onChange={(e) => {
                                                setThisPositionOnly(e.value);
                                                setFirst(0);
                                            }}
                                        />
                                    </div>
                                )}
                                {/* Spacer if no toggle */}
                                {accessType !== 'usermlm' && accessType !== 'orguser' && accessType !== 'orgposition' && <div></div>}

                                {/* Mobile Action Buttons */}
                                <div className="flex gap-2">
                                    <Button
                                        icon="pi pi-filter-slash"
                                        label="Clear"
                                        onClick={handleClearFilters}
                                        className="p-button-outlined p-button-secondary p-button-sm"
                                    />
                                    <Button
                                        icon="pi pi-search"
                                        label="Search"
                                        onClick={handleSearch}
                                        className="p-button-primary p-button-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reports DataTable */}
                <Card>
                    <DataTable
                        value={reports}
                        lazy
                        paginator
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        onPage={(e) => {
                            setFirst(e.first);
                            setRows(e.rows);
                        }}
                        onSort={(e) => {
                            setSortField(e.sortField);
                            setSortOrder(e.sortOrder);
                            setFirst(0);
                        }}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        sortMode="single"
                        removableSort
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                        rowsPerPageOptions={[10, 20, 50]}
                        dataKey="report_id"
                        loading={loading}
                        emptyMessage="No reports found"
                        responsiveLayout="scroll"
                        scrollable
                        scrollHeight="400px"
                        stripedRows
                        size="small"
                        showGridlines
                    >
                        <Column
                            field="sl_no"
                            header="Sl. No"
                            sortable
                            style={{ minWidth: '80px', textAlign: 'center' }}
                            bodyStyle={{ textAlign: 'center' }}
                        />
                        <Column
                            field="report_name"
                            header="Report Name"
                            body={reportNameTemplate}
                            sortable
                            style={{ minWidth: '200px' }}
                        />
                        <Column
                            field="report_id"
                            header="Report ID"
                            sortable
                            style={{ minWidth: '100px' }}
                        />
                        <Column
                            field="lead_id"
                            header="Lead ID"
                            body={(rowData) => rowData.lead_id ? `#${rowData.lead_id}` : '-'}
                            sortable
                            style={{ minWidth: '100px' }}
                        />
                        <Column
                            field="lead_name"
                            header="Lead Name"
                            body={(rowData) => rowData.lead_name || '-'}
                            sortable
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            field="attachment_status"
                            header="Status"
                            body={(rowData) => (
                                <span>{rowData.attachment_status === 'unassigned' ? 'Unassigned' : 'Assigned'}</span>
                            )}
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="fetched_date"
                            header="Date"
                            body={fetchedDateTemplate}
                            sortable
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            header="Actions"
                            body={actionTemplate}
                            exportable={false}
                            alignHeader="center"
                            bodyClassName="text-center"
                            style={{ minWidth: '120px' }}
                        />
                    </DataTable>
                </Card>
            </Dialog>

            {/* Report Details Viewer Dialog */}
            <Dialog
                visible={showReportView}
                onHide={() => {
                    setShowReportView(false);
                    setSelectedReport(null);
                    setReportDetails(null);
                }}
                header={selectedReport ? `${selectedReport.report_name} - Report ID: ${selectedReport.report_id}` : 'Report Details'}
                style={{ width: '90vw', maxHeight: '90vh' }}
                maximizable
                modal
            >
                <ReportViewer
                    selectedReport={selectedReport}
                    reportDetails={reportDetails}
                    loading={detailsLoading}
                />
            </Dialog>
        </>
    );
};

export default MLMUserReportsModal;
